import { useState, useEffect } from 'react';
import { adminApi } from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Users, Search, ToggleLeft, ToggleRight, Shield, Wallet, Loader2, X, KeyRound, Eye, EyeOff } from 'lucide-react';
import Pagination from '../../components/Pagination';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });

const PER_PAGE = 15;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewAll, setViewAll] = useState(false);
  
  // Reset password state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    let filtered = allUsers;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = allUsers.filter(user => 
        user.username.toLowerCase().includes(query) || 
        user.email.toLowerCase().includes(query)
      );
    }
    
    setUsers(filtered);
    setCurrentPage(1);
  }, [searchQuery, allUsers]);

  const fetchUsers = async () => {
    try { 
      const response = await adminApi.getUsers({ per_page: 1000 }); 
      setAllUsers(response.data.data || []);
      setUsers(response.data.data || []);
    } catch (error) { 
      console.error('Failed to fetch users:', error); 
    } finally { 
      setIsLoading(false); 
    }
  };

  const handleToggle = async (userId) => {
    try { await adminApi.toggleUser(userId); toast.success('User status updated'); fetchUsers(); } 
    catch (error) { toast.error('Failed to update user'); }
  };

  const handleMakeAdmin = async (userId) => {
    try { await adminApi.makeAdmin(userId); toast.success('Admin status updated'); fetchUsers(); } 
    catch (error) { toast.error('Failed to update admin status'); }
  };

  const handleAdjustBalance = async () => {
    if (!selectedUser || !adjustAmount) return;
    try { 
      await adminApi.adjustBalance(selectedUser.id, parseFloat(adjustAmount)); 
      toast.success('Balance adjusted'); 
      setAdjustAmount(''); 
      setSelectedUser(null); 
      fetchUsers(); 
    } catch (error) { 
      toast.error('Failed to adjust balance'); 
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResetting(true);
    try {
      await adminApi.resetPassword(selectedUser.id, newPassword);
      toast.success(`Password reset for ${selectedUser.username}`);
      setNewPassword('');
      setShowResetDialog(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPassword(false);
    setShowResetDialog(true);
  };

  // Generate random password
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowPassword(true);
  };

  // Pagination logic
  const totalItems = users.length;
  const totalPages = viewAll ? 1 : Math.ceil(totalItems / PER_PAGE);
  const displayedUsers = viewAll 
    ? users 
    : users.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-white font-['Outfit']">Users ({totalItems})</h1>
        
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search by username or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 bg-[#121212] border-white/10 text-white placeholder:text-zinc-500"
            data-testid="user-search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {searchQuery && (
        <p className="text-sm text-zinc-500">Found {users.length} user{users.length !== 1 ? 's' : ''} matching "{searchQuery}"</p>
      )}

      {viewAll && totalItems > PER_PAGE && (
        <div className="flex items-center justify-between bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg p-3">
          <span className="text-sm text-[#FFD700]">Viewing all {totalItems} users</span>
          <Button variant="outline" size="sm" onClick={() => { setViewAll(false); setCurrentPage(1); }} className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10">
            Show Paginated
          </Button>
        </div>
      )}

      {users.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">{searchQuery ? `No users found matching "${searchQuery}"` : 'No users yet'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {displayedUsers.map((user) => (
              <Card key={user.id} className="bg-[#0A0A0A] border-white/10 hover:border-[#FFD700]/30 transition-colors">
                <CardContent className="p-4 flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                      <span className="text-[#FFD700] font-bold">{user.username.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-white">{user.username}</p>
                        {user.is_admin && <Badge className="bg-[#FFD700] text-black text-xs">Admin</Badge>}
                        {user.is_reseller && <Badge className="bg-purple-500 text-white text-xs">Reseller</Badge>}
                        {!user.is_active && <Badge variant="destructive" className="text-xs">Disabled</Badge>}
                      </div>
                      <p className="text-sm text-zinc-500">{user.email}</p>
                      <p className="text-xs text-zinc-600">Joined {formatDate(user.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-zinc-500">Balance</p>
                      <p className="font-bold text-[#FFD700]">{formatMoney(user.balance)}</p>
                    </div>
                    <div className="flex gap-2">
                      {/* Adjust Balance Dialog */}
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="border-white/10 hover:border-[#FFD700]/50" onClick={() => setSelectedUser(user)} title="Adjust Balance">
                            <Wallet className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#0A0A0A] border-white/10">
                          <DialogHeader><DialogTitle className="text-white">Adjust Balance - {selectedUser?.username}</DialogTitle></DialogHeader>
                          <div className="space-y-4">
                            <p className="text-zinc-400">Current balance: {formatMoney(selectedUser?.balance || 0)}</p>
                            <Input type="number" placeholder="Amount (use negative to deduct)" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} className="bg-[#121212] border-white/10 text-white" />
                            <Button onClick={handleAdjustBalance} className="w-full bg-[#FFD700] text-black font-bold hover:bg-[#E5C100]">Adjust Balance</Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      {/* Reset Password Button */}
                      <Button variant="outline" size="sm" className="border-white/10 hover:border-orange-500/50 text-orange-500" onClick={() => openResetDialog(user)} title="Reset Password">
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      
                      <Button variant="outline" size="sm" className={`border-white/10 ${user.is_admin ? 'text-[#FFD700] border-[#FFD700]/50' : ''}`} onClick={() => handleMakeAdmin(user.id)} title={user.is_admin ? 'Remove admin' : 'Make admin'}>
                        <Shield className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="border-white/10" onClick={() => handleToggle(user.id)} title={user.is_active ? 'Disable user' : 'Enable user'}>
                        {user.is_active ? <ToggleRight className="w-4 h-4 text-green-500" /> : <ToggleLeft className="w-4 h-4 text-red-500" />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {!viewAll && totalPages > 1 && (
            <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} perPage={PER_PAGE} onPageChange={setCurrentPage} onViewAll={() => setViewAll(true)} />
          )}
        </>
      )}

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="bg-[#0A0A0A] border-2 border-orange-500/30">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-orange-500" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-[#121212] rounded-lg">
              <p className="text-sm text-zinc-400">Resetting password for:</p>
              <p className="text-white font-semibold">{selectedUser?.username}</p>
              <p className="text-sm text-zinc-500">{selectedUser?.email}</p>
            </div>
            
            <div>
              <Label className="text-zinc-300">New Password</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 chars)"
                    className="bg-[#121212] border-white/10 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button type="button" variant="outline" onClick={generatePassword} className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10">
                  Generate
                </Button>
              </div>
            </div>

            {newPassword && showPassword && (
              <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-xs text-green-400 mb-1">New password to share with user:</p>
                <code className="text-white font-mono font-bold">{newPassword}</code>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowResetDialog(false)} className="flex-1 border-white/10">
                Cancel
              </Button>
              <Button onClick={handleResetPassword} disabled={resetting || !newPassword || newPassword.length < 6} className="flex-1 bg-orange-500 text-white font-bold hover:bg-orange-600">
                {resetting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Resetting...</> : 'Reset Password'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
